import React, { useState } from "react";

/**
 * Inline SVG sparkline / mini-chart. Three series overlaid (signups, conversions, leads)
 * over the last 30 days, with a hover tooltip showing the date + values.
 *
 * Why inline SVG instead of Recharts: zero additional dependency, faster paint,
 * and the data is small (30 points × 3 series). For richer charts later, swap to Recharts.
 *
 * @param {Array<{date: string, signups: number, conversions: number, leads: number}>} data
 */
export const Sparkline = ({ data = [], width = 1100, height = 220, padding = 28 }) => {
  const [hover, setHover] = useState(null); // index of hovered point

  if (!data.length) return null;

  const innerW = width - padding * 2;
  const innerH = height - padding * 2;
  const max = Math.max(
    1,
    ...data.flatMap((d) => [d.signups, d.conversions, d.leads]),
  );
  const x = (i) => padding + (i * innerW) / Math.max(1, data.length - 1);
  const y = (v) => padding + innerH - (v / max) * innerH;

  const pathFor = (key) =>
    data.map((d, i) => `${i === 0 ? "M" : "L"}${x(i)},${y(d[key])}`).join(" ");

  const COLORS = {
    signups:     { stroke: "#1A2A40", label: "Inscriptions" },
    leads:       { stroke: "#FFD166", label: "Leads PDF" },
    conversions: { stroke: "#E94A3F", label: "Conversions" },
  };

  // Y-axis grid lines (4 ticks)
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((p) => ({
    val: Math.round(max * (1 - p) * 100) / 100,
    py: padding + innerH * p,
  }));
  // X-axis date ticks (1st, 8th, 15th, 22nd, last)
  const xTickIdx = [0, 7, 14, 21, data.length - 1].filter((i) => i < data.length);

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
    } catch { return iso; }
  };

  return (
    <div className="bg-white border-2 border-[#1A2A40]/10 rounded-2xl p-5" data-testid="sparkline">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[#A0AEC0]">Évolution 30 jours</div>
          <div className="font-display font-black text-lg text-[#1A2A40]">Inscriptions · Leads · Conversions</div>
        </div>
        <div className="flex gap-4 text-xs">
          {Object.entries(COLORS).map(([k, c]) => (
            <div key={k} className="inline-flex items-center gap-1.5">
              <span className="inline-block w-3 h-0.5 rounded" style={{ background: c.stroke }} />
              <span className="text-[#4A5568] font-medium">{c.label}</span>
            </div>
          ))}
        </div>
      </div>

      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-auto"
        preserveAspectRatio="none"
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          const px = ((e.clientX - r.left) / r.width) * width;
          const i = Math.round(((px - padding) / innerW) * (data.length - 1));
          setHover(i >= 0 && i < data.length ? i : null);
        }}
      >
        {/* Grid */}
        {yTicks.map((t, i) => (
          <g key={i}>
            <line x1={padding} y1={t.py} x2={width - padding} y2={t.py} stroke="#1A2A40" strokeOpacity="0.06" />
            <text x={padding - 6} y={t.py + 3} fontSize="10" textAnchor="end" fill="#A0AEC0">
              {t.val}
            </text>
          </g>
        ))}
        {/* X-axis date labels */}
        {xTickIdx.map((i) => (
          <text key={i} x={x(i)} y={height - 6} fontSize="10" textAnchor="middle" fill="#A0AEC0">
            {fmtDate(data[i].date)}
          </text>
        ))}
        {/* Line series */}
        {Object.entries(COLORS).map(([k, c]) => (
          <path
            key={k}
            d={pathFor(k)}
            stroke={c.stroke}
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}
        {/* Hover crosshair + dots */}
        {hover !== null && (
          <>
            <line x1={x(hover)} y1={padding} x2={x(hover)} y2={height - padding} stroke="#1A2A40" strokeOpacity="0.15" strokeDasharray="3,3" />
            {Object.entries(COLORS).map(([k, c]) => (
              <circle key={k} cx={x(hover)} cy={y(data[hover][k])} r="4" fill="white" stroke={c.stroke} strokeWidth="2" />
            ))}
          </>
        )}
      </svg>

      {/* Tooltip */}
      <div className="min-h-[44px] mt-2 text-xs flex items-center gap-4 flex-wrap">
        {hover !== null ? (
          <>
            <span className="font-bold text-[#1A2A40]">{fmtDate(data[hover].date)}</span>
            {Object.entries(COLORS).map(([k, c]) => (
              <span key={k} className="inline-flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: c.stroke }} />
                <span className="text-[#4A5568]">{c.label}: <strong className="text-[#1A2A40]">{data[hover][k]}</strong></span>
              </span>
            ))}
          </>
        ) : (
          <span className="text-[#A0AEC0]">Survole le graphique pour voir les valeurs jour par jour.</span>
        )}
      </div>
    </div>
  );
};

export default Sparkline;
